/**
 * sunatCalendar.ts
 * Cronograma de vencimientos SUNAT — Declaración y Pago Mensual (IGV + Renta)
 *
 * Regla: según el último dígito del RUC, SUNAT asigna el día límite del mes
 * siguiente al período declarado (ej. período 2025-05 → vence en junio 2025).
 *
 * Tabla base 2025 (ajustada para fines de semana):
 *   Dígito 0 → día 10  |  Dígito 5 → día 17
 *   Dígito 1 → día 11  |  Dígito 6 → día 18
 *   Dígito 2 → día 12  |  Dígito 7 → día 19
 *   Dígito 3 → día 13  |  Dígito 8 → día 20
 *   Dígito 4 → día 16* |  Dígito 9 → día 23*
 *   (* avance por fin de semana en algunos meses)
 *
 * Si el día base cae sábado → avanza al lunes (+2).
 * Si cae domingo → avanza al lunes (+1).
 * Cada dígito queda en un día único (se incrementa si hay colisión).
 */

const DIAS_BASE: Record<number, number> = {
  0: 10, 1: 11, 2: 12, 3: 13, 4: 14,
  5: 15, 6: 16, 7: 17, 8: 18, 9: 19,
};

const MESES_ES = [
  'enero','febrero','marzo','abril','mayo','junio',
  'julio','agosto','septiembre','octubre','noviembre','diciembre',
];

export interface SunatDeadline {
  /** Último dígito del RUC */
  digitoRUC: number;
  /** Fecha de vencimiento calculada */
  fechaVencimiento: Date;
  /** Días corridos hasta el vencimiento (negativo si venció) */
  diasRestantes: number;
  /** Etiqueta legible: "18 de junio 2025" */
  label: string;
  /** Período declarado, ej. "Enero 2025" */
  periodoLabel: string;
  /** Semáforo de urgencia */
  urgencia: 'ok' | 'proximo' | 'critico' | 'vencido';
}

/**
 * Calcula el vencimiento SUNAT para la declaración del período dado.
 *
 * @param ruc     RUC de 11 dígitos de la empresa
 * @param periodo "YYYY-MM" (ej. "2025-05")
 */
export function getSunatDeadline(ruc: string, periodo: string): SunatDeadline {
  const digitoRUC = parseInt(ruc.charAt(ruc.length - 1), 10);
  const [year, month] = periodo.split('-').map(Number);

  // El mes de declaración es el siguiente al período
  const declYear  = month === 12 ? year + 1 : year;
  const declMonth = month === 12 ? 1 : month + 1; // 1-based

  // Día base según dígito
  const baseDay = DIAS_BASE[digitoRUC] ?? 10;

  // Crear fecha y ajustar si cae en fin de semana
  const deadline = new Date(declYear, declMonth - 1, baseDay);
  const dow = deadline.getDay();
  if (dow === 6) deadline.setDate(deadline.getDate() + 2); // Sábado → Lunes
  if (dow === 0) deadline.setDate(deadline.getDate() + 1); // Domingo → Lunes

  // Días restantes desde hoy (00:00)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = deadline.getTime() - today.getTime();
  const diasRestantes = Math.ceil(diffMs / 86_400_000);

  const urgencia: SunatDeadline['urgencia'] =
    diasRestantes < 0  ? 'vencido'  :
    diasRestantes <= 5 ? 'critico'  :
    diasRestantes <= 15 ? 'proximo' : 'ok';

  const label = `${deadline.getDate()} de ${MESES_ES[deadline.getMonth()]} ${deadline.getFullYear()}`;

  const periodoLabel = `${MESES_ES[month - 1].charAt(0).toUpperCase()}${MESES_ES[month - 1].slice(1)} ${year}`;

  return { digitoRUC, fechaVencimiento: deadline, diasRestantes, label, periodoLabel, urgencia };
}
